import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/Button';
import { BiometricCard } from '@/features/biometric/components/BiometricCard';
import { RiskRangeCard } from '@/features/biometric/components/RiskRangeCard';
import { useBiometricRangesQuery } from '@/features/biometric/hooks/use-biometric-ranges';
import { BIOMETRIC_DEFAULTS } from '@/features/biometric/utils/biometric.defaults';

interface Props {
  canWrite: boolean;
}

export function BiometricDataView({ canWrite }: Props) {
  const { t } = useTranslation('admin');
  const { data, isLoading, isError, refetch, isFetching } =
    useBiometricRangesQuery();

  if (isLoading) {
    return (
      <div className="biometric-data__grid">
        {(['steps', 'sleep', 'heartRate'] as const).map((key) => (
          <div
            key={key}
            className="bio-card bio-card--skeleton"
            aria-busy="true"
          />
        ))}
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="biometric-data__error" role="alert">
        <p>{t('biometricData.toasts.loadError')}</p>
        <Button
          type="button"
          variant="secondary"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          {isFetching
            ? t('biometricData.actions.retrying')
            : t('biometricData.actions.retry')}
        </Button>
      </div>
    );
  }

  const ranges = data;

  return (
    <>
      <div className="biometric-data__grid">
        {/* Row 1: 걸음수, 수면시간 */}
        <BiometricCard
          metricKey="steps"
          title={t('biometricData.metrics.steps')}
          serverValues={ranges.steps ?? BIOMETRIC_DEFAULTS.steps}
          canWrite={canWrite}
        />
        <BiometricCard
          metricKey="sleep"
          title={t('biometricData.metrics.sleep')}
          serverValues={ranges.sleep ?? BIOMETRIC_DEFAULTS.sleep}
          canWrite={canWrite}
        />
        {/* Row 2: 안정시 심박수 (bpm) */}
        <BiometricCard
          metricKey="heartRate"
          title={t('biometricData.metrics.heartRate')}
          serverValues={ranges.heartRate ?? BIOMETRIC_DEFAULTS.heartRate}
          canWrite={canWrite}
        />
        {/* R값 (위험지수) 3구간 임계값 — PRD 09 §3.4. Owns its own query. */}
        <RiskRangeCard canWrite={canWrite} />
      </div>
    </>
  );
}
